import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, TextField, MenuItem, Switch,
  FormControlLabel, Chip, Divider,
} from "@mui/material";
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  HowToReg as ActivationIcon,
  Send as SendIcon,
  Save as SaveIcon,
} from "@mui/icons-material";

// Platform Console → Settings: outgoing email (SMTP), SMS OTP (MSG91) and whether new signups
// must activate their login. The SMTP password and MSG91 auth key are write-only: the server
// says whether one is saved, never what it is, and an empty box keeps the saved one.

const SECURITY = [
  { value: "STARTTLS", label: "STARTTLS (usually port 587)" },
  { value: "SSL", label: "SSL/TLS (usually port 465)" },
  { value: "NONE", label: "None (not recommended)" },
];

function Section({ icon, title, ready, children }) {
  return (
    <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
        {icon}
        <Typography sx={{ fontWeight: 700, flexGrow: 1 }}>{title}</Typography>
        {ready !== undefined && (
          ready ? <Chip size="small" color="success" variant="outlined" label="Working" />
            : <Chip size="small" variant="outlined" label="Off" />
        )}
      </Box>
      {children}
    </Paper>
  );
}

const grid = { display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 1.5 };

export default function PlatformMessagingSettings({ api, onToast }) {
  const [view, setView] = useState(null);
  const [email, setEmail] = useState(null);
  const [sms, setSms] = useState(null);
  const [activation, setActivation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [testTo, setTestTo] = useState({ email: "", mobile: "" });
  const [testing, setTesting] = useState("");
  const [testResult, setTestResult] = useState(null); // { kind, severity, text }

  const apply = (v) => {
    setView(v);
    setEmail({ ...v.email, password: "" });
    setSms({ ...v.sms, authKey: "" });
    setActivation({ ...v.activation });
  };

  const load = useCallback(() => {
    setError(null);
    api("/settings/messaging").then(apply).catch((e) => setError(e.message));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const v = await api("/settings/messaging", { method: "PUT", body: { email, sms, activation } });
      apply(v);
      onToast?.("Settings saved");
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async (kind) => {
    setTesting(kind);
    setTestResult(null);
    try {
      if (kind === "email") {
        await api("/settings/messaging/test-email", { method: "POST", body: { to: testTo.email } });
        setTestResult({ kind, severity: "success", text: `Test email sent to ${testTo.email}. Check the inbox (and spam).` });
      } else {
        const d = await api("/settings/messaging/test-sms", { method: "POST", body: { mobile: testTo.mobile } });
        setTestResult({ kind, severity: "success", text: `MSG91 accepted the SMS. The code sent was ${d.code}.` });
      }
    } catch (e) {
      setTestResult({ kind, severity: "error", text: e.message });
    } finally {
      setTesting("");
    }
  };

  if (error && !view) return <Box sx={{ p: 1.5 }}><Alert severity="error">{error}</Alert></Box>;
  if (!view) return <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}><CircularProgress /></Box>;

  const e = (k) => (ev) => setEmail({ ...email, [k]: ev.target.value });
  const s = (k) => (ev) => setSms({ ...sms, [k]: ev.target.value });
  const dirty = JSON.stringify({ ...view.email, password: "" }) !== JSON.stringify(email)
    || JSON.stringify({ ...view.sms, authKey: "" }) !== JSON.stringify(sms)
    || JSON.stringify(view.activation) !== JSON.stringify(activation);
  const noWay = !view.emailReady && !view.smsReady;

  return (
    <Box sx={{ p: 1.5, maxWidth: 860 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!view.secretsCanBeSaved && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This server has no encryption key (REGULATORY_CREDENTIAL_KEY), so the SMTP password and MSG91 auth key can't be saved.
        </Alert>
      )}

      <Section icon={<ActivationIcon color="primary" />} title="New signups">
        <FormControlLabel
          control={<Switch checked={!!activation.required} onChange={(ev) => setActivation({ ...activation, required: ev.target.checked })} />}
          label="New signups must activate their login before the first sign-in"
        />
        <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.5, mb: 1.5 }}>
          They choose an activation link by email or a code by SMS, from whichever is working below.
          Accounts that already exist are not affected.
        </Typography>
        {activation.required && noWay && (
          <Alert severity="info" sx={{ mb: 1.5 }}>
            Neither email nor SMS is working yet, so new signups can still sign in straight away.
          </Alert>
        )}
        <TextField
          size="small" fullWidth label="Sign-in address for activation links"
          placeholder="https://app.tradelink247.com" value={activation.publicUrl || ""}
          onChange={(ev) => setActivation({ ...activation, publicUrl: ev.target.value })}
          helperText="Leave empty to use the address of the page the person signed up on."
        />
      </Section>

      <Section icon={<EmailIcon color="primary" />} title="Email (SMTP)" ready={view.emailReady}>
        <FormControlLabel
          control={<Switch checked={!!email.enabled} onChange={(ev) => setEmail({ ...email, enabled: ev.target.checked })} />}
          label="Send email"
          sx={{ mb: 1 }}
        />
        <Box sx={grid}>
          <TextField size="small" label="SMTP server" placeholder="smtp.gmail.com" value={email.host} onChange={e("host")} />
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField size="small" label="Port" type="number" value={email.port} onChange={e("port")} sx={{ width: 110 }} />
            <TextField size="small" select label="Security" value={email.security} onChange={e("security")} sx={{ flexGrow: 1 }}>
              {SECURITY.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Box>
          <TextField size="small" label="Username" value={email.username} onChange={e("username")} autoComplete="off" />
          <TextField
            size="small" label="Password" type="password" value={email.password} onChange={e("password")}
            autoComplete="new-password"
            placeholder={view.email.passwordSet ? "Saved. Type to replace" : ""}
            helperText={view.email.passwordSet ? "A password is saved. Leave empty to keep it." : "Not saved yet"}
          />
          <TextField size="small" label="From address" placeholder="no-reply@tradelink247.com" value={email.fromAddress} onChange={e("fromAddress")} />
          <TextField size="small" label="From name" placeholder="TradeLink247" value={email.fromName} onChange={e("fromName")} />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Send a test to" placeholder="you@example.com" value={testTo.email}
            onChange={(ev) => setTestTo({ ...testTo, email: ev.target.value })} sx={{ flex: "1 1 220px" }} />
          <Button startIcon={testing === "email" ? <CircularProgress size={16} /> : <SendIcon />} onClick={() => test("email")}
            disabled={!!testing || !testTo.email.trim() || !view.emailReady || dirty}>
            Send test email
          </Button>
        </Box>
        {testResult?.kind === "email" && <Alert severity={testResult.severity} sx={{ mt: 1.5 }}>{testResult.text}</Alert>}
      </Section>

      <Section icon={<SmsIcon color="primary" />} title="SMS OTP (MSG91)" ready={view.smsReady}>
        <FormControlLabel
          control={<Switch checked={!!sms.enabled} onChange={(ev) => setSms({ ...sms, enabled: ev.target.checked })} />}
          label="Send SMS codes"
          sx={{ mb: 1 }}
        />
        <Box sx={grid}>
          <TextField
            size="small" label="Auth key" type="password" value={sms.authKey} onChange={s("authKey")} autoComplete="new-password"
            placeholder={view.sms.authKeySet ? "Saved. Type to replace" : ""}
            helperText={view.sms.authKeySet ? "An auth key is saved. Leave empty to keep it." : "MSG91 panel → API → Auth key"}
          />
          <TextField size="small" label="OTP template id" value={sms.templateId} onChange={s("templateId")}
            helperText="A DLT-approved OTP template whose text contains ##OTP##" />
          <TextField size="small" label="Country code" value={sms.countryCode} onChange={s("countryCode")}
            helperText="Put in front of 10-digit mobile numbers, e.g. 91" />
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <TextField size="small" label="Send a test to" placeholder="9876543210" value={testTo.mobile}
            onChange={(ev) => setTestTo({ ...testTo, mobile: ev.target.value })} sx={{ flex: "1 1 220px" }} />
          <Button startIcon={testing === "sms" ? <CircularProgress size={16} /> : <SendIcon />} onClick={() => test("sms")}
            disabled={!!testing || !testTo.mobile.trim() || !view.smsReady || dirty}>
            Send test SMS
          </Button>
        </Box>
        {testResult?.kind === "sms" && <Alert severity={testResult.severity} sx={{ mt: 1.5 }}>{testResult.text}</Alert>}
      </Section>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={save} disabled={saving || !dirty}>
          Save settings
        </Button>
        {dirty && <Button onClick={() => apply(view)} disabled={saving}>Discard changes</Button>}
        {dirty && <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Save before sending a test.</Typography>}
      </Box>
    </Box>
  );
}
